import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { ProfileData } from '../types/profileTypes';
import { validateFormData } from '../utils/profileValidation';

export const useProfileForm = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    age: null,
    height_cm: null,
    current_weight_kg: null,
    weight_goal_kg: null,
    fitness_level: 'beginner',
    primary_goals: [],
    experience_level: 'new',
    phone: '',
    emergency_contact: '',
  });

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        age: profile.age,
        height_cm: profile.height_cm,
        current_weight_kg: profile.current_weight_kg,
        weight_goal_kg: profile.weight_goal_kg,
        fitness_level: (profile.fitness_level as 'beginner' | 'intermediate' | 'advanced') || 'beginner',
        primary_goals: profile.primary_goals || [],
        experience_level: (profile.experience_level as 'new' | 'returning' | 'experienced') || 'new',
        phone: profile.phone || '',
        emergency_contact: profile.emergency_contact || '',
      });
    }
  }, [profile]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileData) => {
      if (!user) throw new Error('User not authenticated');
      
      
      
      // Validate data before submission
      const validationErrors = validateFormData(data);
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
      }
      
      // Ensure proper data types
      const sanitizedData = {
        user_id: user.id,
        first_name: data.first_name?.trim() || null,
        last_name: data.last_name?.trim() || null,
        age: data.age,
        height_cm: data.height_cm,
        current_weight_kg: data.current_weight_kg,
        weight_goal_kg: data.weight_goal_kg,
        fitness_level: data.fitness_level,
        primary_goals: Array.isArray(data.primary_goals) ? data.primary_goals : [],
        experience_level: data.experience_level,
        phone: data.phone?.trim() || null,
        emergency_contact: data.emergency_contact?.trim() || null,
      };
      
      
      
      const { error, data: upsertResult } = await supabase
        .from('profiles')
        .upsert([sanitizedData], {
          onConflict: 'user_id'
        });
      
      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }
      
      
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast({
        title: "Profile Updated",
        description: "Your personal information has been saved.",
      });
    },
    onError: (error: any) => {
      
      
      let errorMessage = "Failed to update profile. Please try again.";
      
      // Handle specific error types
      if (error?.message?.includes('Validation failed')) {
        errorMessage = error.message.replace('Validation failed: ', '');
      } else if (error?.message?.includes('duplicate key')) {
        errorMessage = "A profile with this information already exists.";
      } else if (error?.message?.includes('invalid input syntax')) {
        errorMessage = "Invalid data format. Please check your inputs.";
      } else if (error?.code === 'PGRST116') {
        errorMessage = "No profile found. Please try refreshing the page.";
      } else if (error?.code === '23505') {
        errorMessage = "This information is already in use.";
      } else if (error?.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const addGoal = (goal: string) => {
    if (!formData.primary_goals.includes(goal)) {
      setFormData(prev => ({
        ...prev,
        primary_goals: [...prev.primary_goals, goal]
      }));
    }
  };

  const removeGoal = (goal: string) => {
    setFormData(prev => ({
      ...prev,
      primary_goals: prev.primary_goals.filter(g => g !== goal)
    }));
  };

  return {
    formData,
    setFormData,
    isLoading,
    updateProfileMutation,
    addGoal,
    removeGoal,
  };
};